/**
 * DigesterError — the one error type that crosses any boundary (Engine API, worker RPC, HTTP, MCP).
 * Shape and codes: contracts/errors.ts.
 */

import type { DigesterErrorShape, ErrorCode, ErrorDetails } from '../contracts/errors.js';

export class DigesterError extends Error {
  override readonly name = 'DigesterError' as const;
  readonly code: ErrorCode;
  readonly details: ErrorDetails | undefined;

  constructor(code: ErrorCode, message: string, details?: ErrorDetails) {
    super(message);
    this.code = code;
    this.details = details;
  }

  /** Plain, structured-clone and JSON safe. */
  toShape(): DigesterErrorShape {
    return toShape(this);
  }
}

const ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'invalid_input', 'not_found', 'conflict', 'undo_expired', 'plan_limit', 'not_entitled',
  'unauthenticated', 'forbidden', 'rate_limited', 'device_limit', 'fetch_failed', 'fetch_blocked',
  'parse_failed', 'secret_missing', 'source_auth_failed', 'unsupported', 'scoring_unavailable',
  'sync_conflict', 'resync_required', 'clock_skew', 'storage_unavailable', 'internal',
]);

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && ERROR_CODES.has(value);
}

export function isDigesterError(value: unknown): value is DigesterError {
  return value instanceof DigesterError;
}

/** Serialise a DigesterError (or wrap anything else as `internal`). */
export function toShape(error: unknown): DigesterErrorShape {
  const e = toDigesterError(error);
  const shape: DigesterErrorShape = { name: 'DigesterError', code: e.code, message: e.message };
  if (e.details !== undefined) shape.details = e.details;
  return shape;
}

/** Rebuild a DigesterError from its serialised shape (worker / HTTP boundary). Tolerant of junk. */
export function fromShape(shape: unknown): DigesterError {
  if (typeof shape === 'object' && shape !== null) {
    const s = shape as { code?: unknown; message?: unknown; details?: unknown };
    const code = isErrorCode(s.code) ? s.code : 'internal';
    const message = typeof s.message === 'string' ? s.message : 'Unknown error';
    const details = typeof s.details === 'object' && s.details !== null ? (s.details as ErrorDetails) : undefined;
    return new DigesterError(code, message, details);
  }
  return new DigesterError('internal', 'Unknown error');
}

/**
 * Normalise anything thrown into a DigesterError. A non-DigesterError becomes `internal` whose
 * message names the original error (never its stack or data).
 */
export function toDigesterError(error: unknown): DigesterError {
  if (error instanceof DigesterError) return error;
  if (error instanceof Error) return new DigesterError('internal', `Internal error: ${error.message}`);
  return new DigesterError('internal', 'Internal error');
}
